import axios from 'axios';
import { BASE_URL } from './helpers';

export type RenderTemplateKind = 'carousel' | string;
export type RenderTemplateSource = 'builtin' | 'user_saved' | 'agent_generated' | string;
export type RenderTemplateStatus = 'draft' | 'active' | 'archived' | string;
export type RenderProposalStatus = 'draft' | 'validated' | 'approved' | 'rejected' | 'failed' | string;

export interface RenderTemplate {
  id: string;
  user_id?: string | null;
  name: string;
  description?: string;
  kind: RenderTemplateKind;
  source: RenderTemplateSource;
  status: RenderTemplateStatus;
  config: Record<string, unknown>;
  preview_url?: string | null;
  created_from_clip_id?: string | null;
  created_from_instruction?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface CreateRenderTemplateRequest {
  id?: string;
  name: string;
  description?: string;
  kind?: RenderTemplateKind;
  source?: RenderTemplateSource;
  status?: RenderTemplateStatus;
  config?: Record<string, unknown>;
  preview_url?: string | null;
  created_from_clip_id?: string | null;
  created_from_instruction?: string | null;
}

export interface RenderProposal {
  id: string;
  clip_id?: string | null;
  user_id?: string | null;
  kind: string;
  status: RenderProposalStatus;
  instruction: string;
  source_template_id?: string | null;
  metadata_patch_json: Record<string, unknown>;
  template_config_json: Record<string, unknown>;
  preview_output_refs_json?: unknown;
  validation_report_json?: Record<string, unknown>;
  created_at?: string;
  approved_at?: string | null;
}

export interface CreateRenderProposalRequest {
  id?: string;
  clip_id?: string | null;
  kind?: string;
  status?: RenderProposalStatus;
  instruction?: string;
  source_template_id?: string | null;
  metadata_patch_json?: Record<string, unknown>;
  template_config_json?: Record<string, unknown>;
  preview_output_refs_json?: unknown;
  validation_report_json?: Record<string, unknown>;
}

export interface PatchRenderProposalStatusRequest {
  status: RenderProposalStatus;
  validation_report_json?: Record<string, unknown>;
}

const listRenderTemplates = (
  options: { kind?: RenderTemplateKind; includeArchived?: boolean } = {}
): Promise<RenderTemplate[]> =>
  axios
    .get<RenderTemplate[]>(`${BASE_URL}/render-templates`, {
      params: {
        kind: options.kind,
        include_archived: options.includeArchived || undefined,
      },
    })
    .then((res) => res.data || []);

const createRenderTemplate = (request: CreateRenderTemplateRequest): Promise<RenderTemplate> =>
  axios.post<RenderTemplate>(`${BASE_URL}/render-templates`, request).then((res) => res.data);

const updateRenderTemplate = (
  templateId: string,
  request: CreateRenderTemplateRequest
): Promise<RenderTemplate> =>
  axios
    .put<RenderTemplate>(`${BASE_URL}/render-templates/${encodeURIComponent(templateId)}`, request)
    .then((res) => res.data);

const deleteRenderTemplate = (templateId: string): Promise<void> =>
  axios.delete(`${BASE_URL}/render-templates/${encodeURIComponent(templateId)}`).then(() => undefined);

const listRenderProposals = (clipId?: string): Promise<RenderProposal[]> =>
  axios
    .get<RenderProposal[]>(`${BASE_URL}/render-proposals`, {
      params: { clip_id: clipId || undefined },
    })
    .then((res) => res.data || []);

const createRenderProposal = (request: CreateRenderProposalRequest): Promise<RenderProposal> =>
  axios.post<RenderProposal>(`${BASE_URL}/render-proposals`, request).then((res) => res.data);

const patchRenderProposalStatus = (
  proposalId: string,
  request: PatchRenderProposalStatusRequest
): Promise<RenderProposal> =>
  axios
    .patch<RenderProposal>(`${BASE_URL}/render-proposals/${encodeURIComponent(proposalId)}/status`, request)
    .then((res) => res.data);

const RenderTemplatesAPI = {
  listRenderTemplates,
  createRenderTemplate,
  updateRenderTemplate,
  deleteRenderTemplate,
  listRenderProposals,
  createRenderProposal,
  patchRenderProposalStatus,
};

export default RenderTemplatesAPI;
